import { useLazyQuery } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { RecordUpdateLifecycle } from "~/common/types/graphql";
import {
  PrepareRecordUpdateDataImportProjectDocument,
  type PrepareRecordUpdateDataImportProjectQuery,
  type PrepareRecordUpdateDataImportProjectQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

/** The read-only blast-radius preview returned by the prepare query. */
export type RecordUpdatePreview = PrepareRecordUpdateDataImportProjectQuery["cnutPrepareRecordUpdateDataImportProject"];

/**
 * Fires the read-only record-update preview on demand (not on mount) for the
 * given RecordIDs and optional evidence IDs in the current tenant. Returns the resolved blast radius plus
 * the confirmationToken the proceed step must echo back. Writes nothing.
 */
export const useRecordUpdatePreview = () => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();
  const [preview, setPreview] = useState<RecordUpdatePreview | null>(null);
  const [runPreview, { loading }] = useLazyQuery<
    PrepareRecordUpdateDataImportProjectQuery,
    PrepareRecordUpdateDataImportProjectQueryVariables
  >(PrepareRecordUpdateDataImportProjectDocument, { fetchPolicy: "no-cache" });

  const run = useCallback(
    async (
      recordIds: string[],
      lifecycle: RecordUpdateLifecycle = "edit",
      evidenceIds: string[] = [],
    ): Promise<RecordUpdatePreview | null> => {
      try {
        if (!tenantId) {
          throw new Error("Tenant is not resolved");
        }

        const { data } = await runPreview({ variables: { input: { tenantId, recordIds, evidenceIds, lifecycle } } });
        const result = data?.cnutPrepareRecordUpdateDataImportProject ?? null;
        setPreview(result);
        return result;
      } catch (e) {
        toast.error(translateError(t, e));
        return null;
      }
    },
    [runPreview, tenantId, t],
  );

  const reset = useCallback(() => setPreview(null), []);

  return { preview, loading, run, reset };
};
